import { useEffect, useState, useContext } from "react";
import {
  useNavigate,
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Table, Input, Modal, Tooltip, Button } from "antd";
import moment from "moment";
import { UserPlus, Globe, Briefcase, MessageCircle } from "lucide-react";

import { PAGE_LENGTH, subscriptionPlans } from "../../config";
import copy from "../../assets/images/webToLead/copy.png";
import emailSync from "../../assets/svg/icons/send.svg";
import TrialModal from "../../common/TrialModal";
import {
  selectedRow,
  saveSearchAction,
  getExecutiveEmployeeList,
} from "../../actionCreator/executiveListingActionCreater";
import {
  submitLead,
  getEmployeeViewableStatusUpdate,
} from "../../actionCreator/executiveDetailsActionCreator";
import Loader from "../loader";
import {
  getUserInfo,
  getSubscriptionUserInfo,
  getPartialPhoneNumber,
} from "../../utils/utils";
import TagExecutive from "./TagExecutive";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import EmailForm from "../../common/EmailForm";
import SubscriptionModal from "../web2lead/SubscriptionModal";
import CopyText from "../web2lead/CopyText";
import { getTagList } from "../../actionCreator/dashboardActionCreator";
import addedToLead from "../../assets/svg/added-to-lead.svg";
import useUpgradeAlert from "../../common/hooks/useUpgradeAlert";
import viewEmail from "../../assets/svg/view-email.svg";
import viewEmailChecked from "../../assets/svg/view-email-checked.svg";
import mobile from "../../assets/svg/mobile.svg";
import mobileChecked from "../../assets/svg/mobile-checked.svg";
import CustomPagination from "../../common/CustomPagination";

const ExecutiveContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { showAlert } = useAlert();
  const { id: accountId, validTo } = getSubscriptionUserInfo();
  const currentDate = moment();
  const validToDate = moment(validTo);
  const isExpired = validToDate.isBefore(currentDate);
  const {
    isAuthenticated: isLoggedIn,
    isEmailSetupView,
    subscription,
  } = useContext(AuthContext);
  const showUpgradeAlert = useUpgradeAlert();

  const [showEmail, setShowEmail] = useState({});
  const [showPhone, setShowPhone] = useState({});
  const [employeeIdsData, setEmployeeIdsData] = useState([]);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const [subModal, setSubModal] = useState(false);
  const [executiveEmployeeList, setExecutiveEmployeeList] = useState();
  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [searchValues, setSearchValues] = useState({
    tagname: "",
    description: "",
    tagError: "",
  });

  const getEmployeeViewableData = useSelector(
    (state) => state.executiveDetailsReducer?.executiveEmployeeViewableStatus,
  );
  const companyFilterList = useSelector(
    (state) => state.executiveListingReducer,
  );
  const loading = useSelector((state) => state.CommonReducer.loading);
  const selectedRecords = useSelector(
    (state) => state.executiveListingReducer.selectedRecords,
  );

  const executiveSelectionList = useSelector(
    (state) => state.executiveListingReducer?.executiveSelectionList,
  );

  const updateEmailStatus = async (showEmail, row) => {
    if (!accountId || isExpired) {
      setSubModal(true);
    } else {
      setShowEmail({ ...showEmail, [row.key]: true });
      const res = await dispatch(getEmployeeViewableStatusUpdate("Email", row));
      if (res?.status === 400) {
        showAlert("error", res?.title);
      }
    }
  };

  const updatePhoneStatus = async (showPhone, row) => {
    setShowPhone({ ...showPhone, [row.key]: true });
    const res = await dispatch(getEmployeeViewableStatusUpdate("Mobile", row));
    if (res?.status === 400) {
      showAlert("error", res?.title);
    }
  };

  const openInfoModel = () => {
    setOpenModal({ info: null, open: !isLoggedIn || !accountId });
  };

  const postLeads = async (record, isLeadSubmit) => {
    const { id } = getUserInfo();
    let leadPayload = {
      accountId: accountId,
      firstname: record.firstname,
      lastname: record.lastname,
      fullname: record.fullname,
      title: record.title,
      client: record.client,
      emailId: record.emailId,
      phoneNo: record.phoneNo,
      bio: record.bio,
      description: record.description,
      userId: id,
      employeeId: record.id,
      address: record?.company?.address,
      companyId: record?.company?.id,
      companyName: record?.company?.name,
      industryId: record?.company?.industry?.id,
      industryText: record?.company?.industry?.name,
      empSizeId: record?.company?.range?.name,
      city: record?.company?.city,
      state: record?.company?.state,
      country: record?.company?.country,
      status: 11,
    };
    //status 11 for new lead
    if (!isLeadSubmit) {
      const res = await dispatch(submitLead(leadPayload, employeeIdsData));

      if (res?.status === 201) {
        const allParams = {};
        for (const [key, value] of searchParams.entries()) {
          allParams[key] = value;
        }

        dispatch(
          getExecutiveEmployeeList({
            ...allParams,
            page: allParams?.page ? allParams?.page - 1 : 0,
          }),
        );
      } else {
        showAlert("error", res);
      }
    }
  };

  useEffect(() => {
    if (executiveSelectionList?.length) {
      dispatch(
        selectedRow(executiveSelectionList?.map((d) => ({ ...d, key: d?.id }))),
      );
    } else {
      dispatch(selectedRow([]));
    }
  }, [dispatch, executiveSelectionList]);

  const renderSocialLinks = (socialLinks) => {
    return (
      <div className="tw-flex tw-items-center tw-gap-2">
        {socialLinks?.map((link) => {
          if (link?.name === "facebook") {
            return (
              <Link
                key={link?.proifileUrl}
                to={link?.proifileUrl}
                target="_blank"
                className="tw-text-slate-400 hover:tw-text-[#1877F2] tw-transition-colors tw-duration-200"
              >
                <Globe size={18} />
              </Link>
            );
          } else if (link?.name === "Linkedin") {
            return (
              <Link
                key={link?.proifileUrl}
                to={link?.proifileUrl}
                target="_blank"
                className="tw-text-slate-400 hover:tw-text-[#0A66C2] tw-transition-colors tw-duration-200"
              >
                <Briefcase size={18} />
              </Link>
            );
          } else if (link?.name === "twitter") {
            return (
              <Link
                key={link?.proifileUrl}
                to={link?.proifileUrl}
                target="_blank"
                className="tw-text-slate-400 hover:tw-text-[#1DA1F2] tw-transition-colors tw-duration-200"
              >
                <MessageCircle size={18} />
              </Link>
            );
          }
          return null;
        })}
      </div>
    );
  };

  useEffect(() => {
    let data = [];
    let empIdsArr = [];
    companyFilterList?.executiveEmployeeList?.forEach((record) => {
      empIdsArr = [...empIdsArr, record.id];
      data = [
        ...data,
        {
          key: record.id,
          id: record.id,
          executiveData: record,
          name: record,
          company: record?.company?.name,
          location: `${record?.city ? record?.city + "," : ""} ${
            record?.state ? record?.state : ""
          }`,
          email: record?.emailId,
          phone: record?.company?.phoneNo,
          mobile: record.phoneNo,
          designation: record?.title,
          social: renderSocialLinks(record?.socialLinks),
          isdownloadedEmail: record?.isdownloadedEmail,
          isdownloadedMobile: record?.isdownloadedMobile,
          directDial: record,
        },
      ];
    });

    setExecutiveEmployeeList(data);
    setEmployeeIdsData(empIdsArr);
  }, [companyFilterList]);

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => d.key !== record.key,
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, record]));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const keys = changeRows?.map((d) => d.key);
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => !keys.includes(d.key),
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, ...changeRows]));
      }
    },
    getCheckboxProps: (record) => {
      return {
        disabled: record.name === "Disabled User", // Column configuration not to be checked
        name: record.name,
      };
    },
    selectedRowKeys: selectedRecords?.map((d) => d?.key),
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      if (
        [subscriptionPlans?.trail].includes(
          subscription?.subscriptionPackage?.name,
        )
      ) {
        showUpgradeAlert("this page");
      } else {
        setShowModal(false);
      }
    } else {
      setShowModal(true);
    }
  };

  const onPageChange = (page, pageSize) => {
    checkLoginStatus();
    if (!isLoggedIn) {
      if (page > 1 || pageSize > PAGE_LENGTH)
        setOpenModal({ info: null, open: true });
      setShowModal(false);
      return false;
    } else {
      if (
        ![subscriptionPlans?.trail].includes(
          subscription?.subscriptionPackage?.name,
        )
      ) {
        searchParams.set("page", page);
        searchParams.set("size", pageSize);
        navigate(`${location.pathname}?${searchParams.toString()}`);
      }
    }
  };

  const getDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-", name);
    navigate(`/executive-details/${id}/${cname}`);
  };

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
  };

  const onConfrim = async () => {
    if (!searchValues.tagname) {
      setSearchValues({
        ...searchValues,
        tagError: "error",
      });
    } else {
      const { id, email, login } = getUserInfo();
      const queryObject = Object.fromEntries(searchParams.entries());
      const { page, size, ...filteredQuery } = queryObject;
      const payload = {
        accountId: login,
        dataDump: JSON.stringify(filteredQuery),
        fullName: searchValues.tagname,
        emailId: email,
        source: "Executive",
        userId: id,
      };
      const res = await dispatch(saveSearchAction(payload));
      if (res?.status === 201) {
        showAlert("success", "Saved Search Created!");
        setSearchValues({
          tagname: "",
          description: "",
          tagError: "",
        });
        setShowModal(false);
      } else {
        showAlert("error", res?.response?.data?.title);
        setSearchValues({
          ...searchValues,
          tagError: res?.response?.data?.title,
        });
      }
    }
  };

  const onInputChange = (e) => {
    setSearchValues({
      ...searchValues,
      [e.target.name]: e.target.value,
    });
  };

  const redirectToSignup = () => {
    setShowModal(false);
    setOpenModal(false);
    navigate("/signup");
  };

  const closeTagModal = () => {
    setShowTagModal(false);
  };

  const columns = [
    {
      title: <div className="companyname">Executive Name</div>,
      dataIndex: "name",
      fixed: "left",
      width: "20%",
      render: (record, row, index) => {
        const name = record?.fullname
          ? record.fullname
          : record.firstname + " " + record.lastname;
        return (
          <div
            className="namecol"
            onClick={() => getDetails(row.key, record?.fullname)}
          >
            <div className="tw-flex tw-mr-2 tw-items-center tw-justify-center tw-rounded-full tw-border tw-size-[40px] tw-min-w-[40px] tw-border-[#CDD2DF] tw-border-solid tw-uppercase tw-font-xl tw-font-semibold">
              {record?.firstname?.[0]}
              {record?.lastname?.[0]}
            </div>
            <Tooltip title={name}>
              <span className="tw-truncate tw-w-full">{name}</span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "company",
      render: (record) => {
        return (
          <span className="tw-whitespace-nowrap tw-flex tw-items-center">
            <Tooltip title={record}>
              <span className="tw-truncate tw-w-full">{record}</span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Designation",
      dataIndex: "designation",
      render: (record) => {
        return (
          <span className="tw-whitespace-nowrap tw-flex tw-items-center">
            <Tooltip title={record}>
              <span className="tw-truncate tw-w-full">{record}</span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      className: "email",
      width: 180,
      render: (text, row) => {
        let checkLeadSubmitted = row?.executiveData?.isLead;
        return isLoggedIn ? (
          <>
            <div
              className={`tw-flex tw-items-center tw-gap-3 ${showEmail[row.key] && row.key === getEmployeeViewableData?.id && "tw-justify-between"}`}
            >
              {showEmail[row.key] && row.key === getEmployeeViewableData?.id ? (
                <div className="tw-flex tw-items-center tw-gap-3">
                  <Tooltip title={getEmployeeViewableData?.emailId}>
                    <span>
                      {getEmployeeViewableData?.emailId?.length > 12
                        ? `${getEmployeeViewableData?.emailId.substring(0, 10)}...`
                        : getEmployeeViewableData?.emailId}
                    </span>
                  </Tooltip>
                  <div className="tw-bg-white tw-border tw-border-solid tw-border-border-secondary tw-rounded-lg tw-flex tw-items-center tw-p-1">
                    <CopyText
                      text={getEmployeeViewableData?.emailId}
                      content={
                        <Tooltip title="Copy Email">
                          <img
                            src={copy}
                            alt=""
                            className="cursor-pointer tw-h-[25px] tw-w-[25px] tw-p-[5px] tw-max-w-[25px]"
                          />
                        </Tooltip>
                      }
                    />

                    {isEmailSetupView && (
                      <>
                        <div className="tw-w-[1px] tw-h-[22px] tw-bg-[#C7C7CC] tw-mx-1.5 tw-shrink-0" />
                        <EmailForm
                          recipientEmail={getEmployeeViewableData?.emailId}
                          empId={getEmployeeViewableData?.id}
                          payload={getEmployeeViewableData}
                          convertToLead={!row?.executiveData?.isLead}
                          onConvertToLead={() =>
                            postLeads(row?.name, checkLeadSubmitted)
                          }
                        >
                          <Tooltip title="Send Email">
                            <img
                              src={emailSync}
                              alt=""
                              className="tw-cursor-pointer tw-h-[23px] tw-w-[23px] tw-p-[5px] tw-max-w-[23px]"
                            />
                          </Tooltip>
                        </EmailForm>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <span
                    className="white-space-nowrap cursor-pointer"
                    onClick={() => updateEmailStatus(showEmail, row)}
                  >
                    View Email{" "}
                  </span>
                  <img
                    src={row?.isdownloadedEmail ? viewEmailChecked : viewEmail}
                    alt=""
                    className="cursor-pointer"
                    onClick={() => updateEmailStatus(showEmail, row)}
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <img
            src={viewEmail}
            alt=""
            className="cursor-pointer"
            onClick={() => openInfoModel()}
          />
        );
      },
    },
    {
      title: "Phone",
      dataIndex: "phone",
      className: "phone",
      render: (text, row) => {
        const number = getPartialPhoneNumber(text);
        return (
          <span className="tw-whitespace-nowrap tw-flex tw-items-center">
            <Tooltip title={number}>
              <span className="tw-truncate tw-w-full">{number}</span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      render: (record, row) => {
        return isLoggedIn &&
          ![subscriptionPlans?.trail].includes(
            subscription?.subscriptionPackage?.name,
          ) ? (
          <>
            {!!row?.executiveData?.phoneNo &&
            row?.executiveData?.phoneNo !== "" ? (
              <>
                {showPhone[row.key] &&
                row.key === getEmployeeViewableData?.id ? (
                  <div className="d-flex align-items-center gap-10">
                    <Tooltip title={getEmployeeViewableData?.phoneNo}>
                      <span className="phoneValue fs-12 pl-1 white-space-nowrap cursor-pointer">
                        {getEmployeeViewableData?.phoneNo?.substring(0, 10)}
                        ...
                      </span>
                    </Tooltip>

                    <div className="email-grp-btn tw-px-[3px]">
                      <CopyText
                        text={getEmployeeViewableData?.phoneNo}
                        content={
                          <Tooltip title="Copy ">
                            <img
                              src={copy}
                              alt=""
                              className="cursor-pointer tw-h-[25px] tw-w-[25px] tw-p-[5px] tw-max-w-[25px]"
                            />
                          </Tooltip>
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <img
                    src={row?.isdownloadedMobile ? mobileChecked : mobile}
                    alt=""
                    onClick={() => updatePhoneStatus(showPhone, row)}
                  />
                )}
              </>
            ) : (
              <>-</>
            )}
          </>
        ) : (
          <img src={mobile} alt="" onClick={() => openInfoModel()} />
        );
      },
    },
    {
      title: "Social",
      dataIndex: "social",
    },
    {
      title: "",
      dataIndex: "name",
      width: "5%",
      render: (record, row) => {
        let checkLeadSubmitted = row?.executiveData?.isLead;
        return isLoggedIn && accountId ? (
          <div
            className="cursor-pointer"
            onClick={() => postLeads(record, checkLeadSubmitted)}
          >
            {checkLeadSubmitted ? (
              <Tooltip title="Added to lead">
                <img src={addedToLead} alt="" />
              </Tooltip>
            ) : (
              <Tooltip title="Add to lead">
                <UserPlus size={18} className="tw-text-slate-600 tw-cursor-pointer" />
              </Tooltip>
            )}
          </div>
        ) : (
          <Tooltip title="Add to lead">
            <UserPlus
              size={18}
              className="tw-text-slate-600 tw-cursor-pointer"
              onClick={() => openInfoModel()}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <SubscriptionModal
        isOpen={subModal}
        handleCancel={() => setSubModal(false)}
      />
      <div id="content-wrapper" className="tw-flex tw-flex-col">
        <div id="content">
          <div className="tw-pl-0 tw-pr-0">
            {!loading ? (
              <div className="tw-custom-table-container tw-h-[calc(100vh-215px)] tw-flex tw-flex-col">
                <Table
                  rowSelection={{
                    type: "checkbox",
                    ...rowSelection,
                  }}
                  columns={columns?.map((col) => ({
                    ...col,
                    onCell: (record, rowIndex) => ({
                      className: "word-break-keep-all",
                    }),
                  }))}
                  className="tw-custom-table tw-executive-search-table"
                  dataSource={executiveEmployeeList}
                  pagination={false}
                  tableLayout="fixed"
                  scroll={false}
                />
                <CustomPagination
                  total={companyFilterList?.totalExecutiveCount}
                  current={searchParams.get("page") || 1}
                  pageSize={
                    parseInt(PAGE_LENGTH) >
                    parseInt(companyFilterList?.totalExecutiveCount)
                      ? companyFilterList?.totalExecutiveCount
                      : searchParams.get("size")
                        ? searchParams.get("size")
                        : PAGE_LENGTH
                  }
                  onChange={onPageChange}
                  noBorder={true}
                />
              </div>
            ) : (
              <Loader />
            )}
          </div>
        </div>
      </div>

      <TagExecutive
        selectedRecords={selectedRecords?.map((d) => d?.key)}
        showTagModal={showTagModal && isLoggedIn}
        closeTagModal={closeTagModal}
        callback={(userId) => dispatch(getTagList("EXECUTIVE"))}
      />

      {showModal ? (
        isLoggedIn ? (
          <Modal
            title={null}
            width={"400px"}
            className="delete-popup"
            closable={true}
            open={showModal}
            onCancel={closeModal}
            onOk={onConfrim}
            footer={null}
            centered
          >
            <div className="tw-bg-slate-50 tw-text-slate-800 tw-text-lg tw-font-bold tw-py-4 tw-px-6 tw-rounded-t-2xl tw-border tw-border-solid tw-border-slate-100/50">
              Save Search
            </div>
            <div className="pop-up errorformcontainer tw-p-6">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                  Search Name
                </span>

                <Input
                  name="tagname"
                  status={searchValues?.tagError}
                  value={searchValues.tagname}
                  placeholder="Name"
                  onChange={onInputChange}
                  className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200"
                />
              </div>
            </div>
            <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
              <Button
                className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
                onClick={() => {
                  closeModal();
                }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
                onClick={onConfrim}
              >
                Ok
              </Button>
            </div>
          </Modal>
        ) : (
          <TrialModal
            openModal={showModal}
            closeModal={closeModal}
            redirectToSignup={redirectToSignup}
            redirect={true}
          />
        )
      ) : (
        ""
      )}

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          isSubscriptionMissing
        />
      )}
    </>
  );
};

export default ExecutiveContent;
